import { useForm, router } from '@inertiajs/react'
import DOMPurify from 'dompurify'
import InvestorPortalLayout from '@/layouts/InvestorPortalLayout'
import { useEffect, useState, useCallback } from 'react'

interface TermsSnapshot {
  property_type?: string
  ltv?: number
  rate?: number
  term_months?: number
  [key: string]: unknown
}

interface Offering {
  id: number
  lender_name: string
  status: string
  total_amount: number
  min_commitment: number | null
  deal_memo: string | null
  terms_snapshot: TermsSnapshot
  committed_amount: number
  remaining_amount: number
  percent_funded: number
  loan_number: string
  loan_rate: number
  loan_term: number
  published_at: string | null
}

interface Commitment {
  id: number
  amount: number
  committed_at: string
  status: string
}

interface Props {
  offering: Offering
  commitment: Commitment | null
  investor_name: string
}

const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const statusColors: Record<string, string> = {
  open: 'bg-emerald-100 text-emerald-800',
  locked: 'bg-amber-100 text-amber-800',
  funded: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
}

export default function Show({ offering, commitment, investor_name }: Props) {
  const [liveCommitted, setLiveCommitted] = useState(offering.committed_amount)
  const [liveRemaining, setLiveRemaining] = useState(offering.remaining_amount)
  const [livePercent, setLivePercent] = useState(offering.percent_funded)

  const pollAvailability = useCallback(() => {
    fetch(`/investor-portal/deal_offerings/${offering.id}/availability`, {
      credentials: 'same-origin',
    })
      .then((r) => r.json())
      .then((data) => {
        setLiveCommitted(data.committed_amount)
        setLiveRemaining(data.remaining_amount)
        setLivePercent(data.percent_funded)
      })
      .catch(() => {})
  }, [offering.id])

  useEffect(() => {
    if (offering.status !== 'open') return
    const interval = setInterval(pollAvailability, 15_000)
    return () => clearInterval(interval)
  }, [offering.status, pollAvailability])

  const commitForm = useForm({ amount: '' })
  const [withdrawing, setWithdrawing] = useState(false)

  function handleCommit(e: React.FormEvent) {
    e.preventDefault()
    commitForm.post(`/investor-portal/deal_offerings/${offering.id}/soft_commitments`)
  }

  function handleWithdraw() {
    if (!commitment) return
    setWithdrawing(true)
    router.delete(
      `/investor-portal/deal_offerings/${offering.id}/soft_commitments/${commitment.id}`,
      { onFinish: () => setWithdrawing(false) }
    )
  }

  const terms = offering.terms_snapshot || {}

  return (
    <InvestorPortalLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">{offering.lender_name}</p>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="text-2xl font-bold text-gray-900">
              Deal — Loan #{offering.loan_number}
            </h1>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[offering.status] || 'bg-gray-100 text-gray-800'}`}
            >
              {offering.status.charAt(0).toUpperCase() + offering.status.slice(1)}
            </span>
          </div>
        </div>

        {/* Terms Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Deal Terms
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <TermItem label="Offering" value={fmtMoney(offering.total_amount)} />
            <TermItem label="Rate" value={`${offering.loan_rate}%`} />
            <TermItem label="Term" value={`${offering.loan_term} mo`} />
            {terms.ltv != null && <TermItem label="LTV" value={`${terms.ltv}%`} />}
            {terms.property_type && (
              <TermItem label="Property" value={terms.property_type} />
            )}
            {offering.min_commitment && (
              <TermItem label="Min Commit" value={fmtMoney(offering.min_commitment)} />
            )}
          </div>
        </div>

        {/* Deal Memo */}
        {offering.deal_memo && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Deal Memo
            </h2>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(offering.deal_memo || ''),
              }}
            />
          </div>
        )}

        {/* Progress Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Funding Progress</span>
            <span className="text-sm font-semibold text-emerald-700">{livePercent}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${Math.min(livePercent, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {fmtMoney(liveCommitted)} committed of {fmtMoney(offering.total_amount)} —{' '}
            <span className="font-medium text-gray-700">{fmtMoney(liveRemaining)} remaining</span>
          </p>
        </div>

        {/* Commit / Status Section */}
        {offering.status === 'open' && !commitment && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Submit Your Commitment
            </h2>
            <form onSubmit={handleCommit} className="flex items-end gap-3">
              <div className="flex-1">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ($)
                </label>
                <input
                  id="amount"
                  type="number"
                  min={offering.min_commitment || 1}
                  max={liveRemaining}
                  step="0.01"
                  placeholder={offering.min_commitment ? `Min ${fmtMoney(offering.min_commitment)}` : 'Enter amount'}
                  value={commitForm.data.amount}
                  onChange={(e) => commitForm.setData('amount', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={commitForm.processing}
                className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {commitForm.processing ? 'Submitting...' : 'Commit'}
              </button>
            </form>
          </div>
        )}

        {commitment && (
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-800">Your Commitment</p>
                <p className="text-2xl font-bold text-emerald-900 mt-1">
                  {fmtMoney(commitment.amount)}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Committed on {new Date(commitment.committed_at).toLocaleDateString()}
                </p>
              </div>
              {offering.status === 'open' && (
                <button
                  onClick={handleWithdraw}
                  disabled={withdrawing}
                  className="px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-lg border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors"
                >
                  {withdrawing ? 'Withdrawing...' : 'Withdraw'}
                </button>
              )}
            </div>
          </div>
        )}

        {offering.status === 'locked' && commitment && (
          <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
            <p className="text-sm font-medium text-amber-800 mb-1">Offering Locked — Participation Confirmed</p>
            <p className="text-sm text-amber-700">
              Your commitment of {fmtMoney(commitment.amount)} has been converted into a permanent loan participation.
              You will receive distributions when payments are made on this loan.
            </p>
          </div>
        )}

        {offering.status !== 'open' && !commitment && (
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 text-center">
            <p className="text-sm text-gray-500">
              This offering is currently <strong>{offering.status}</strong> and is not accepting new commitments.
            </p>
          </div>
        )}
      </div>
    </InvestorPortalLayout>
  )
}

function TermItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{value}</p>
    </div>
  )
}
